/**
 * Where each numbered box sits on EPA Form 8700-22 and its 8700-22A
 * continuation sheet.
 *
 * A constant, not data. This describes a fixed-layout federal form, so it does
 * not vary by tenant, document or scan, and nothing writes it at runtime. Putting
 * it in the database would buy a migration to seed it, a query to read it, and
 * the ability for it to drift between environments — for a value that changes
 * only when EPA revises the form, which is a code change reviewed like any other.
 *
 * It is deliberately not per-document geometry. Extraction is an LLM over the
 * file buffer, so there are no bounding boxes and no OCR coordinates; getting
 * real ones would need a second pass (Textract `AnalyzeDocument`) at meaningful
 * cost and latency. The form's fixed layout makes that unnecessary for the job
 * this does, which is pointing rather than cropping.
 *
 * **The highlight is a hint.** Coordinates are normalized to the whole page, so a
 * rotated, cropped, skewed or photographed scan lands the outline imprecisely —
 * and those are exactly the documents a reviewer needs help with. It must
 * therefore always be a soft outline the reviewer can look past and pan away
 * from, never a crop and never something the UI treats as authoritative.
 *
 * **Vertical geometry is measured; horizontal column edges are still estimated.**
 * The bands and the table's origin come from two real 8700-22 scans, read with
 * `pdftotext -bbox` and cross-checked against each other — they agreed within
 * half a percent of page height, which is the scan-to-scan variance the padding
 * absorbs. The earlier hand-estimated values put the middle of the form about
 * thirteen percent of page height too low, roughly three table rows, so Box 11
 * on the first waste line outlined somewhere around Box 14.
 *
 * The column x edges were not measurable the same way: the printed column
 * headers are centred over their cells, so where a label starts is not where its
 * cell does. They are close but unverified. `FormRegionSheet` (staff → Observe →
 * Form Regions) draws the whole map over a document so they can be corrected by
 * eye.
 *
 * **The continuation sheet is entirely unverified** — none of the documents to
 * hand carried one, so 8700-22A's numbers remain hand-estimated.
 */

/**
 * A rectangle on a page, normalized 0-1 from the top-left corner.
 *
 * Top-left origin rather than the PDF's own bottom-left: the viewer hands us a
 * page element, so a percentage-positioned overlay needs no coordinate flip and
 * no knowledge of the scan's DPI, and it scales with zoom for free.
 */
export interface BoxRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Which printed form a box number belongs to. */
export type ManifestFormVariant = "8700-22" | "8700-22A";

/** Left and right edges of the printed frame, inside the page margin. */
const FRAME = { left: 0.03, right: 0.97 } as const;
const FRAME_WIDTH = FRAME.right - FRAME.left;

function band(y: number, height: number): BoxRegion {
  return { x: FRAME.left, y, width: FRAME_WIDTH, height };
}

function cell(x: number, y: number, width: number, height: number): BoxRegion {
  return { x, y, width, height };
}

/**
 * The waste-line table on the main form: four rows between the column headers
 * and Box 14.
 */
const MAIN_TABLE = { top: 0.309, rowHeight: 0.0455, rows: 4 } as const;

/**
 * The continuation sheet's table. It carries more rows than the main form in
 * less vertical space, because the sheet has no certification blocks above it.
 */
const CONTINUATION_TABLE = { top: 0.3, rowHeight: 0.045, rows: 8 } as const;

/**
 * Column edges of the waste-line table, shared by both sheets — the continuation
 * sheet repeats the main form's columns under different numbers (27-31 for
 * 9a-13).
 */
const COLUMNS = {
  hm: { x: 0.03, width: 0.06 },
  description: { x: 0.09, width: 0.37 },
  containers: { x: 0.46, width: 0.12 },
  quantity: { x: 0.58, width: 0.12 },
  unit: { x: 0.7, width: 0.06 },
  wasteCodes: { x: 0.76, width: 0.21 },
} as const;

/**
 * Which table column a box number addresses, per sheet.
 *
 * Keyed by sheet rather than pooled, because the two number the same columns
 * differently and a pooled map would happily resolve Box 11 on a continuation
 * sheet that has no Box 11 — pointing the reviewer at a column the page does not
 * label that way.
 */
const TABLE_COLUMN: Record<ManifestFormVariant, Record<string, keyof typeof COLUMNS>> = {
  "8700-22": {
    "9a": "hm",
    "9b": "description",
    "10": "containers",
    "11": "quantity",
    "12": "unit",
    "13": "wasteCodes",
  },
  "8700-22A": {
    "27a": "hm",
    "27b": "description",
    "28": "containers",
    "29": "quantity",
    "30": "unit",
    "31": "wasteCodes",
  },
};

/**
 * Boxes that are not part of the waste-line table, and so have one fixed place
 * on the sheet.
 */
const FIXED_REGIONS: Record<ManifestFormVariant, Record<string, BoxRegion>> = {
  "8700-22": {
    // Header row: generator ID, page count, emergency phone, tracking number.
    // The two inner edges come from the printed labels for Boxes 2 and 3, which
    // are the only ones in this row the text layer places unambiguously.
    "1": cell(0.03, 0.071, 0.42, 0.033),
    "2": cell(0.45, 0.071, 0.06, 0.033),
    "3": cell(0.51, 0.071, 0.21, 0.033),
    "4": cell(0.72, 0.071, 0.25, 0.033),

    "5": band(0.104, 0.065),
    // Boxes 6 and 7 are one printed row each and the same height; the pair spans
    // the gap between Box 5's content and Box 8's label.
    "6": band(0.169, 0.027),
    "7": band(0.196, 0.027),
    "8": band(0.223, 0.061),

    "14": band(0.491, 0.056),
    "15": band(0.547, 0.072),
    "16": band(0.619, 0.038),
    "17": band(0.657, 0.075),
    // Box 18 covers 18a, 18b and 18c — the whole discrepancy block, which is why
    // it is the tallest band on the sheet.
    "18": band(0.732, 0.133),
    "19": band(0.865, 0.046),
    "20": band(0.911, 0.046),
  },
  "8700-22A": {
    // Header row: generator ID, page number, tracking number.
    "21": cell(0.03, 0.06, 0.39, 0.05),
    "22": cell(0.42, 0.06, 0.2, 0.05),
    "23": cell(0.62, 0.06, 0.35, 0.05),

    "24": band(0.11, 0.06),
    "25": band(0.17, 0.05),
    "26": band(0.22, 0.05),

    "32": band(0.67, 0.06),
    "33": band(0.73, 0.05),
    "34": band(0.78, 0.05),
    "35": band(0.83, 0.05),
    "36": band(0.88, 0.05),
  },
};

/**
 * Where a box sits on its sheet, or null when the box is not one this map knows.
 *
 * `rowIndex` places a waste-line box in the table — the table's boxes repeat down
 * it, so Box 11 alone does not say which line's quantity is meant. A row past the
 * end of the table clamps to the last row rather than running off the page, since
 * the highlight is a hint and pointing at the bottom of the right table beats
 * pointing at nothing.
 */
export function boxRegion(
  variant: ManifestFormVariant,
  box: string,
  rowIndex?: number
): BoxRegion | null {
  const fixed = FIXED_REGIONS[variant][box];
  if (fixed) return fixed;

  const column = TABLE_COLUMN[variant][box];
  if (column == null) return null;

  const table = variant === "8700-22" ? MAIN_TABLE : CONTINUATION_TABLE;
  const row = Math.min(Math.max(rowIndex ?? 0, 0), table.rows - 1);
  const { x, width } = COLUMNS[column];

  return { x, y: table.top + row * table.rowHeight, width, height: table.rowHeight };
}

/**
 * How much slack to leave around a box when framing it.
 *
 * Two terms, because the error this absorbs does not scale with the box. A
 * cropped, photographed or skewed scan shifts the whole form by some fraction of
 * the *page* — two or three percent of page height is ordinary — and that offset
 * is the same whether the target is a full-width band or a table cell 6% wide.
 * A ratio alone would give the tightest cells, which need slack most, the least
 * of it: 20% of 6% is barely over one percent of the page.
 *
 * So the floor is what protects small boxes and the ratio is what keeps large
 * ones from being framed too tightly.
 */
export const REGION_PADDING = { boxRatio: 0.2, pageFloor: 0.04 } as const;

/**
 * The least page a frame must show, as a fraction of page height.
 *
 * A cap on magnification rather than on the region: framing a 4.7%-tall table
 * cell exactly would put the viewer at roughly sixteen times page width, where an
 * offset of two percent of page height moves the value most of a viewport away.
 * Keeping a quarter of the page in view bounds that to about four times, which
 * leaves a band of context above and below the target for the reader's eye to
 * close the gap with.
 *
 * The consequence is that full-width bands — most of the form — barely zoom at
 * all, and the table cells where the form is dense zoom the most. Which is the
 * right way round.
 */
export const MIN_VISIBLE_PAGE_HEIGHT = 0.25;

/** A region grown by `REGION_PADDING`, clamped to the page. */
export function paddedRegion(
  region: BoxRegion,
  padding: { boxRatio: number; pageFloor: number } = REGION_PADDING
): BoxRegion {
  const padX = Math.max(region.width * padding.boxRatio, padding.pageFloor);
  const padY = Math.max(region.height * padding.boxRatio, padding.pageFloor);

  const left = Math.max(0, region.x - padX);
  const top = Math.max(0, region.y - padY);
  const right = Math.min(1, region.x + region.width + padX);
  const bottom = Math.min(1, region.y + region.height + padY);

  return { x: left, y: top, width: right - left, height: bottom - top };
}

/**
 * What a region needs from the viewer's scale.
 *
 * `fitsAt` is the largest scale at which the whole padded region is still on
 * screen, uncapped. `wanted` is that capped by `MIN_VISIBLE_PAGE_HEIGHT`, which
 * is the scale to actually use. They differ for a small region, where the cap
 * stops the viewer magnifying a table cell to sixteen times page width — and
 * keeping both is what lets a caller tell "already fits" from "already zoomed
 * as far as we allow".
 */
export interface RegionZoom {
  wanted: number;
  fitsAt: number;
}

/**
 * The viewer scale that frames a region, capped so context stays on screen.
 *
 * Page dimensions are the intrinsic ones — what the page measures at scale 1 —
 * because that is the unit the viewer's own scale is expressed in.
 *
 * Returns a scale to zoom *to*, not a decision about whether to. A caller that
 * would be zooming out, or that the reviewer has already zoomed themselves,
 * should leave the viewer alone: following the caret is help, and help that
 * overrides a deliberate action is not help.
 */
export function regionZoomScale({
  region,
  container,
  page,
  minVisiblePageHeight = MIN_VISIBLE_PAGE_HEIGHT,
}: {
  region: BoxRegion;
  /** The scrolling viewport, in pixels. */
  container: { width: number; height: number };
  /** The page at scale 1, in pixels. */
  page: { width: number; height: number };
  minVisiblePageHeight?: number;
}): RegionZoom | null {
  if (container.width <= 0 || container.height <= 0) return null;
  if (page.width <= 0 || page.height <= 0) return null;

  const padded = paddedRegion(region);
  if (padded.width <= 0 || padded.height <= 0) return null;

  // Whichever axis runs out first decides, so the whole padded region fits.
  const fitsAt = Math.min(
    container.width / (page.width * padded.width),
    container.height / (page.height * padded.height)
  );
  const cap = container.height / (page.height * minVisiblePageHeight);

  return { wanted: Math.min(fitsAt, cap), fitsAt };
}

/**
 * Whether to re-frame, and at what scale — or null to leave the viewer alone.
 *
 * Leaving it alone is the common answer, and deliberately so. Following the caret
 * is help; help that throws the pane around while someone tabs sixty fields, or
 * that undoes a closer look they took themselves, is not.
 *
 * `ourLast` is the scale this component set the last time it framed something.
 * Comparing it against the live scale is how a zoom we caused is told apart from
 * one the reviewer caused — the viewer reports both the same way.
 */
export function zoomDecision({
  zoom,
  current,
  ourLast,
}: {
  /** What the region needs, from `regionZoomScale`. */
  zoom: RegionZoom | null;
  /** What the viewer is at now, or null before it has reported a scale. */
  current: number | null;
  /** The scale we set last, or null if we have not set one for this document. */
  ourLast: number | null;
}): number | null {
  if (zoom == null) return null;
  if (current == null) return zoom.wanted;

  // Off the screen entirely: the region is bigger than the viewport at this
  // magnification, so the outline points at something the reviewer cannot see.
  // Pulling back is the one case worth overruling their own zoom for — a frame
  // they cannot look at is not a closer look, it is a lost one.
  if (current > zoom.fitsAt) return zoom.wanted;

  // The reviewer has zoomed since we last did, so from here the magnification is
  // theirs to choose.
  if (ourLast != null && current !== ourLast) return null;

  // It already fits and they are no closer than we put them, so magnifying
  // further is the only thing left worth doing — and only if it gains something.
  if (zoom.wanted <= current) return null;

  return zoom.wanted;
}

/** Every box this map knows for a sheet, for the verification harness. */
export function knownBoxes(variant: ManifestFormVariant): string[] {
  return [...Object.keys(FIXED_REGIONS[variant]), ...Object.keys(TABLE_COLUMN[variant])];
}
